import React, { useState, useEffect, useCallback } from 'react';
import { Settings2, Save, Check, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const FIELD_LABELS = {
  free_daily_messages: { label: 'Daily AI Messages (Free)', desc: 'Max messages per day for free users' },
  free_monthly_sessions: { label: 'Monthly Sessions (Free)', desc: 'Max sessions per month for free users' },
  free_max_starred_spots: { label: 'Max Starred Spots (Free)', desc: 'Max favorited spots for free users' },
  free_max_boards: { label: 'Max Boards (Free)', desc: 'Max boards in garage for free users' },
  free_max_suits: { label: 'Max Suits (Free)', desc: 'Max suits in garage for free users' },
  free_forecast_days: { label: 'Forecast Days (Free)', desc: 'Days of forecast for free users (Pro gets 7)' },
};

export default function AdminBillingConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/billing/admin/gate-config`);
      if (res.ok) setConfig(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/api/billing/admin/gate-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch {} finally { setSaving(false); }
  };

  if (loading || !config) return <div className="text-xs text-muted-foreground py-8 text-center">Loading config...</div>;

  return (
    <div data-testid="admin-billing-config">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-amber-400/60" />
          <p className="text-xs font-heading font-semibold">Feature Gate Config</p>
        </div>
        <button
          data-testid="save-gate-config"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-40 font-medium"
        >
          {saved ? <><Check className="w-3 h-3" /> Saved</> : saving ? <><RefreshCw className="w-3 h-3 animate-spin" /> Saving</> : <><Save className="w-3 h-3" /> Save</>}
        </button>
      </div>

      <div className="space-y-3">
        {Object.entries(FIELD_LABELS).map(([key, { label, desc }]) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/[0.025] border border-white/5 rounded-xl p-3"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-white">{label}</p>
                <p className="text-[9px] text-muted-foreground/50 mt-0.5">{desc}</p>
              </div>
              <input
                data-testid={`gate-${key}`}
                type="number"
                min={0}
                value={config[key] ?? ''}
                onChange={e => setConfig(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center outline-none focus:border-primary/50 transition-colors"
              />
            </div>
          </motion.div>
        ))}
      </div>

      <p className="text-[9px] text-muted-foreground/40 mt-4">
        Changes take effect immediately. Pro users bypass all limits.
      </p>
    </div>
  );
}
